<script setup>
defineProps({
  peleas: { type: Array, default: () => [] },
  bloqueado: { type: Boolean, default: false },
});

defineEmits(["seleccionar"]);

const estadoLabel = {
  pendiente: "Pendiente",
  en_curso: "En Curso",
  pausada: "Pausada",
  tiempo_agotado: "T. Agotado",
  finalizada: "Finalizada",
};
</script>

<template>
  <div v-if="peleas.length" class="proximas-section">
    <div class="titulo-row">
      <span class="titulo">PRÓXIMAS PELEAS</span>
      <span v-if="bloqueado" class="locked-badge">🔒 Combate en curso</span>
    </div>

    <div class="lista" :class="{ 'is-locked': bloqueado }">
      <div
        v-for="p in peleas"
        :key="p.pelea_id"
        class="card"
        :class="[p.estado, { clickable: !bloqueado }]"
        @click="!bloqueado && $emit('seleccionar', p.pelea_id)"
      >
        <span class="vs">
          <b>{{ p.rojo?.nombre || "BYE" }}</b>
          <em>vs</em>
          <b>{{ p.azul?.nombre || "BYE" }}</b>
        </span>

        <span class="chip" :class="p.estado">
          Ronda {{ p.ronda }} &nbsp;|&nbsp; {{ estadoLabel[p.estado] || p.estado }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;600;700&display=swap");

.proximas-section {
  padding: 12px 16px 14px;
  background: linear-gradient(180deg, #f8fafc, #eef3f8);
  border-top: 1px solid #dbe3ee;
  flex-shrink: 0;
  border-radius: 12px;
}

.titulo-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.titulo {
  font-family: "DM Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000000;
}

.locked-badge {
  font-family: "DM Mono", monospace;
  font-size: 10px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #fff7ed;
  border: 1px solid #fdba74;
  color: #c2410c;
  letter-spacing: 0.5px;
  font-weight: 700;
}

.lista {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  transition: opacity 0.25s;
}

.lista.is-locked {
  opacity: 0.55;
  pointer-events: none;
  filter: grayscale(0.2);
}

.card {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  transition: all 0.2s ease;
  font-size: 13px;
  color: #000000;
  min-width: 180px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}

.card.clickable {
  cursor: pointer;
}

.card.clickable:hover {
  transform: translateY(-2px);
  border-color: #b8c7db;
  background: #ffffff;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.1);
}

.card.pendiente {
  border-left: 5px solid #38bdf8;
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 100%);
}

.card.en_curso {
  border-left: 5px solid #22c55e;
  background: linear-gradient(180deg, #ffffff 0%, #f0fdf4 100%);
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.08), 0 10px 22px rgba(34, 197, 94, 0.12);
}

.card.pausada {
  border-left: 5px solid #f59e0b;
  background: linear-gradient(180deg, #ffffff 0%, #fffbeb 100%);
}

.card.tiempo_agotado {
  border-left: 5px solid #ef4444;
  background: linear-gradient(180deg, #ffffff 0%, #fef2f2 100%);
}

.card.finalizada {
  border-left: 5px solid #94a3b8;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.vs {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  line-height: 1.35;
}

.vs b {
  font-family: "DM Sans", sans-serif;
  font-weight: 700;
  color: #000000;
}

.vs em {
  font-style: normal;
  color: #000000;
  font-size: 11px;
  font-weight: 600;
  opacity: 0.75;
}

.chip {
  font-family: "DM Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  width: fit-content;
  letter-spacing: 0.4px;
  border: 1px solid transparent;
}

.chip.pendiente {
  background: #e0f2fe;
  color: #075985;
  border-color: #7dd3fc;
}

.chip.en_curso {
  background: #dcfce7;
  color: #166534;
  border-color: #86efac;
}

.chip.pausada {
  background: #fef3c7;
  color: #b45309;
  border-color: #fcd34d;
}

.chip.tiempo_agotado {
  background: #fee2e2;
  color: #b91c1c;
  border-color: #fca5a5;
}

.chip.finalizada {
  background: #e2e8f0;
  color: #475569;
  border-color: #cbd5e1;
}

::-webkit-scrollbar {
  height: 6px;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}
::-webkit-scrollbar-track {
  background: transparent;
}

@media (max-width: 768px) {
  .card {
    min-width: 155px;
    padding: 10px 12px;
  }

  .vs b {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .proximas-section {
    padding: 10px 12px 12px;
  }

  .card {
    min-width: 135px;
    font-size: 11px;
    padding: 9px 10px;
  }

  .chip {
    font-size: 9px;
    padding: 4px 8px;
  }
}
</style>
